import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { Client, ClientFormData } from '@/lib/types/admin';

const COLLECTION_NAME = 'clients';

// Get all clients (admin)
export async function getClients(): Promise<Client[]> {
  const q = query(collection(db, COLLECTION_NAME), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Client[];
}

// Get active clients (public)
export async function getActiveClients(): Promise<Client[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('active', '==', true),
    orderBy('order', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Client[];
}

// Get single client by ID
export async function getClientById(id: string): Promise<Client | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Client;
  }
  return null;
}

// Create new client
export async function createClient(
  data: ClientFormData,
  userId: string
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: userId,
  });
  return docRef.id;
}

// Update client
export async function updateClient(
  id: string,
  data: Partial<ClientFormData>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

// Delete client
export async function deleteClient(id: string): Promise<void> {
  // Get the client first to check for logo
  const client = await getClientById(id);

  // Delete logo from storage if exists
  if (client?.logo) {
    try {
      const logoRef = ref(storage, client.logo);
      await deleteObject(logoRef);
    } catch (error) {
      console.error('Error deleting client logo:', error);
    }
  }

  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

// Upload client logo
export async function uploadClientLogo(
  file: File,
  clientName: string
): Promise<string> {
  const timestamp = Date.now();
  const safeName = clientName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const extension = file.name.split('.').pop();
  const path = `clients/${safeName}-${timestamp}.${extension}`;

  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

// Delete client logo from storage
export async function deleteClientLogo(logoUrl: string): Promise<void> {
  try {
    const logoRef = ref(storage, logoUrl);
    await deleteObject(logoRef);
  } catch (error) {
    console.error('Error deleting client logo:', error);
  }
}

// Get next order number
export async function getNextClientOrder(): Promise<number> {
  const clients = await getClients();
  if (clients.length === 0) return 1;
  const maxOrder = Math.max(...clients.map((c) => c.order));
  return maxOrder + 1;
}

// Reorder clients
export async function reorderClients(
  orderedIds: string[]
): Promise<void> {
  const updatePromises = orderedIds.map((id, index) =>
    updateClient(id, { order: index + 1 })
  );
  await Promise.all(updatePromises);
}
